<template>
  <div class="date-picker" :class="{ 'read-only': readonly, disabled: disabled, danger: isInvalid, 'is-invalid': isInvalid }">
    <input
      class="date-picker-input"
      :class="{ 'read-only': readonly, disabled: disabled, 'border-danger': isInvalid }"
      v-model.lazy="inputValue"
      ref="datePickerInput"
      v-if="!buttonOnly"
      :readonly="readonly"
      :disabled="disabled"
      :placeholder="placeholder" />
    <button class="date-picker-button" :class="{ disabled: disabled }" :popovertarget="`date-picker-${id}`" ref="datePickerButton" :disabled="disabled">
      <Icon icon="Calendar" />
    </button>
    <div
      :id="`date-picker-${id}`"
      class="date-picker-menu"
      :class="{ 'is-open': isOpen }"
      popover
      @toggle="toggleHandler"
      ref="datePickerMenu"
      :style="{
        transform: `translate(${menuPosition.x}px, ${menuPosition.y}px)`
      }">
      <div class="current-selection" :class="{ 'has-value': localValue }">
        {{ localValue ? $d(localValue.toDate(), "longDateWithWeekDay") : $t("noDateSelected") }}
      </div>
      <hr class="divider" />
      <div class="current-month-year">
        <div class="month">{{ $t(`${navigationDate.format("MMMM").toLowerCase()}`) }}</div>
        <div class="year">{{ navigationDate.format("YYYY") }}</div>
      </div>
      <div class="calendar">
        <div class="weekdays">
          <div class="weekday-text">{{ $t("weekday.mon") }}</div>
          <div class="weekday-text">{{ $t("weekday.tue") }}</div>
          <div class="weekday-text">{{ $t("weekday.wed") }}</div>
          <div class="weekday-text">{{ $t("weekday.thu") }}</div>
          <div class="weekday-text">{{ $t("weekday.fri") }}</div>
          <div class="weekday-text">{{ $t("weekday.sat") }}</div>
          <div class="weekday-text">{{ $t("weekday.sun") }}</div>
        </div>
        <div class="calendar-grid">
          <div class="calendar-grid-row" v-for="(row, rowIndex) in calculatedGridArray" :key="`row-${rowIndex}`">
            <div
              :data-date="entry.dateString"
              :aria-selected="entry.dateString == localValue?.format(DEFAULT_DATE_V_MODEL_FORMAT)"
              class="calendar-grid-entry"
              :class="{
                'current-month': entry.isInCurrentMonth,
                today: today.format(DEFAULT_DATE_V_MODEL_FORMAT) == entry.dateString,
                selected: entry.dateString == localValue?.format(DEFAULT_DATE_V_MODEL_FORMAT)
              }"
              v-for="(entry, index) in row"
              :key="`entry-${rowIndex}-${index}`"
              @click="selectEntry(entry)">
              {{ entry.day }}
            </div>
          </div>
        </div>
      </div>
      <hr class="divider" />
      <div class="calendar-navigation">
        <b-btn class="btn-icon mono pre-year" @click="switchDate(-1, 'year')" :title="$t('datepicker.lastYear')">
          <Icon icon="ChevronDoubleLeft" />
        </b-btn>
        <b-btn class="btn-icon mono pre-month" @click="switchDate(-1, 'month')" :title="$t('datepicker.lastMonth')">
          <Icon icon="ChevronLeft" />
        </b-btn>
        <b-btn class="btn-icon mono to-today" @click="jumptoToday" :title="$t('datepicker.currentMonth')">
          <Icon icon="CurrentMonthSheet" />
        </b-btn>
        <b-btn class="btn-icon mono post-year" @click="switchDate(1, 'month')" :title="$t('datepicker.nextMonth')">
          <Icon icon="ChevronRight" />
        </b-btn>
        <b-btn class="btn-icon mono post-month" @click="switchDate(1, 'year')" :title="$t('datepicker.nextYear')">
          <Icon icon="ChevronDoubleRight" />
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from "@common/components/Icon.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { useUserSettings } from "@common/composable/useRootStore";
import { Guid } from "@util/guid";
import _ from "lodash";
import moment, { Moment, unitOfTime } from "moment";
import { computed, nextTick, reactive, ref, watch } from "vue";

//#region Store

const { userSettings } = useUserSettings();
const { contentLanguages } = useContentLanguages();

//#endregion

//#region Constants
const DEFAULT_DATE_V_MODEL_FORMAT = "YYYY-MM-DD";

//#endregion

//#region General
type CalendarGridModel = { day: number; isInCurrentMonth: boolean; dateString: string };

const id = Guid.new();
const datePickerButton = ref<HTMLButtonElement | null>(null);
const datePickerMenu = ref<HTMLDivElement | null>(null);
const datePickerInput = ref<HTMLInputElement | null>(null);

const props = defineProps<{
  value: string | null | undefined;
  buttonOnly?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  isInvalid?: boolean;
}>();
const emits = defineEmits(["input", "change"]);

const localeDateFormat = computed(() =>
  contentLanguages.value.available.find((x) => x.languageCode == userSettings.value.CurrentLocale)?.shortDateFormat.toUpperCase()
);

//#endregion

//#region Viewmodel
const localValue = ref<Moment | null>(null);
const lastValidValue = ref<Moment | null>(null);

const menuPosition = reactive({ x: 0, y: 0 });
const isOpen = ref(false);

const navigationDate = ref(moment());
const today = moment();
//#endregion

//#region LocalValue

const inputValue = computed({
  get: () => {
    return localValue.value?.format(localeDateFormat.value);
  },
  set: (nv) => {
    if (nv === "" || !nv) {
      localValue.value = null;
    } else {
      const date = moment(nv, localeDateFormat.value);

      if (date.isValid()) {
        navigationDate.value = date;
        localValue.value = date;
        lastValidValue.value = date;
      } else {
        localValue.value = _.cloneDeep(lastValidValue.value);
        if (datePickerInput.value && !lastValidValue.value) datePickerInput.value.value = "";
      }
    }
  }
});

watch(
  () => props.value,
  (nv) => {
    if (nv === "" || !nv) {
      localValue.value = null;
    } else {
      const date = moment(nv);

      if (date.isValid()) {
        navigationDate.value = date;
        localValue.value = date;
        lastValidValue.value = date;
      } else {
        localValue.value = _.cloneDeep(lastValidValue.value);
        if (datePickerInput.value && !lastValidValue.value) datePickerInput.value.value = "";
      }
    }
  },
  { immediate: true }
);
watch(
  localValue,
  (nv) => {
    emits("input", nv?.format(DEFAULT_DATE_V_MODEL_FORMAT));
  },
  { deep: true }
);
//#endregion

//#region VisualContruction

const toggleHandler = (event: ToggleEvent) => {
  isOpen.value = event.newState == "open";
  calculatePositionOfMenu();
};

const calculatePositionOfMenu = () => {
  menuPosition.x = 0;
  menuPosition.y = 0;
  nextTick(() => {
    if (datePickerMenu.value && datePickerButton.value) {
      const toggleRect = datePickerButton.value.getBoundingClientRect();
      const menuRect = datePickerMenu.value.getBoundingClientRect();
      const x = toggleRect.x - menuRect.right + toggleRect.width;
      let y = toggleRect.y - menuRect.y + toggleRect.height;
      if (menuRect.bottom + y > window.innerHeight) {
        y = toggleRect.y - menuRect.bottom;
      }
      menuPosition.x = x;
      menuPosition.y = y;
    }
  });
};

const calculatedGridArray = computed(() => {
  const result: Array<CalendarGridModel[]> = [];
  const manipulationDate = moment(navigationDate.value);
  manipulationDate.locale("de");

  const endOfMonth = moment(manipulationDate.endOf("month"));
  const startOfMonth = moment(manipulationDate.startOf("month"));

  for (let i = 0; i <= 5; i++) {
    const week: CalendarGridModel[] = [];
    for (let i = 0; i <= 6; i++) {
      const entryDate = manipulationDate.weekday(i);
      entryDate.add(1, "second");
      const entry: CalendarGridModel = {
        day: parseInt(entryDate.format("DD")),
        isInCurrentMonth: entryDate.isBetween(startOfMonth, endOfMonth),
        dateString: entryDate.format(DEFAULT_DATE_V_MODEL_FORMAT)
      };
      week.push(entry);
    }
    result.push(week);
    manipulationDate.add(1, "week");
  }
  return result;
});
//#endregion

//#region Interaction

const toggle = () => {
  datePickerMenu.value?.togglePopover(!isOpen.value);
};
const show = () => {
  datePickerMenu.value?.showPopover();
};
const hide = () => {
  datePickerMenu.value?.hidePopover();
};

const selectEntry = (entry: CalendarGridModel) => {
  if (entry.isInCurrentMonth) {
    localValue.value = moment(entry.dateString);
    emits("change", localValue.value?.format(DEFAULT_DATE_V_MODEL_FORMAT));
    hide();
  }
};

const switchDate = (amount: number, unitOfTime: unitOfTime.Base) => {
  navigationDate.value = moment(navigationDate.value.add(amount, unitOfTime));
};

const jumptoToday = () => {
  navigationDate.value = moment();
};

//#endregion

//#region Expose
defineExpose({
  toggle,
  hide,
  show
});
//#endregion
</script>
